import { AnimatePresence, motion } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { useEffect, useState } from 'react';

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setScrolled(window.scrollY > 50);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const menuItems = [
    { label: 'Work', href: '#work' },
    { label: 'Services', href: '#services' },
    { label: 'About', href: '#about' },
    { label: 'Blog', href: '#blog' },
    { label: 'Contact', href: '#contact' }
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMenuOpen(false);
    }
  };

  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5, ease: [0.6, 0.05, 0.01, 0.9] }}
        className={`fixed top-0 left-0 right-0 z-40 px-4 py-4 sm:px-6 sm:py-6 md:px-12 md:py-8 transition-all duration-300 ${
          scrolled ? 'bg-black/90 backdrop-blur-md' : 'bg-transparent'
        }`}
      >
        <div className="flex justify-between items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="cursor-pointer"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            <img
              src="/full logo.png"
              alt="Rukn Alfahalah Technology"
              className="h-13 sm:h-13 md:h-14 w-auto object-contain"
            />
          </motion.div>

          <div className="flex items-center gap-3 sm:gap-2">
            <motion.button
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="hidden md:block text-white text-xs sm:text-sm tracking-wider hover:text-orange-500 transition-colors duration-300 font-light"
            >
              LET'S TALK
            </motion.button>

            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              onClick={() => setMenuOpen(!menuOpen)}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white flex items-center justify-center hover:bg-orange-500 transition-colors duration-300 group will-change-transform"
              aria-label="Menu"
            >
              <Menu className="text-black group-hover:text-white transition-colors w-5 h-5 sm:w-6 sm:h-6" />
            </motion.button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-black/98 z-50 flex items-center justify-center backdrop-blur-sm overflow-y-auto"
          >
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.3 }}
              onClick={() => setMenuOpen(false)}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className="absolute top-4 right-4 sm:top-6 sm:right-6 md:top-8 md:right-12 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white flex items-center justify-center hover:bg-orange-500 transition-colors duration-300 group z-10 will-change-transform"
              aria-label="Close menu"
            >
              <X className="text-black group-hover:text-white transition-colors w-5 h-5 sm:w-6 sm:h-6" />
            </motion.button>

            {/* Watermark logo */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.03 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.8 }}
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
            >
              <img
                src="/logo.png"
                alt=""
                className="w-64 sm:w-96 md:w-[500px] h-auto object-contain"
              />
            </motion.div>

            <nav className="text-center relative z-10 px-4">
              <ul className="space-y-4 sm:space-y-6">
                {menuItems.map((item, index) => (
                  <motion.li
                    key={item.label}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -30 }}
                    transition={{
                      duration: 0.4,
                      delay: 0.1 + index * 0.1,
                      ease: [0.6, 0.05, 0.01, 0.9]
                    }}
                  >
                    <motion.button
                      onClick={() => scrollToSection(item.href)}
                      whileHover={{ scale: 1.05, x: 10 }}
                      whileTap={{ scale: 0.95 }}
                      className="text-white text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold tracking-wider hover:text-orange-500 transition-colors duration-300 relative group will-change-transform"
                    >
                      {item.label}
                    </motion.button>
                  </motion.li>
                ))}
              </ul>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -30 }}
                transition={{ duration: 0.4, delay: 0.6 }}
                className="mt-12 sm:mt-16 text-gray-400 text-xs sm:text-sm space-y-2 font-light"
              >
                <motion.p whileHover={{ color: '#ff5722' }} className="transition-colors cursor-pointer">
                  ayushaj11220@gmail.com
                </motion.p>
                <motion.p whileHover={{ color: '#ff5722' }} className="transition-colors cursor-pointer">
                  +971 56 747 2118
                </motion.p>
                <p className="text-[10px] sm:text-xs mt-4">
                  Al Quasais Industrial Area 4, Dubai, UAE
                </p>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
